import { createClient } from '@devjuanes/matuclient';

const url = process.env.REACT_APP_MATUDB_URL || 'https://db.matudb.com';
const projectId =
  process.env.REACT_APP_MATUDB_PROJECT_ID || 'ddb24135-9c94-423f-ab54-c1c25e719105';
const apiKey =
  process.env.REACT_APP_MATUDB_API_KEY ||
  'mb_64274822fd362deaf09c7b1a2be2a7743003faa03ae3d76249b8c584e14306e3';
const schema =
  process.env.REACT_APP_MATUDB_SCHEMA || 'proj_ddb24135_9c94_423f_ab54_c1c25e719105';

export const db = createClient({
  url,
  projectId,
  apiKey,
  schema,
});

export const TABLES = {
  free: 'abet',
  live: 'abetlive',
  vip: 'abetvip',
  predictions: 'dp_predictions',
  follows: 'dp_prediction_follows',
  saves: 'mp_saves',
  comments: 'mp_comments',
  tickets: 'mp_tickets',
};

export function newId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `p_${Date.now().toString(16)}${Math.random().toString(16).slice(2)}`;
}

function isoDate(value) {
  if (!value) return null;
  const text = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;
  const match = text.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (match) return `${match[3]}-${match[2]}-${match[1]}`;
  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) {
    return /^\d{4}-\d{2}-\d{2}/.test(text) ? text.slice(0, 10) : null;
  }
  // MatuDB serializa la columna date a las 22:00Z del día anterior (medianoche UTC+2).
  const shifted = new Date(parsed.getTime() + 12 * 60 * 60 * 1000);
  return shifted.toISOString().slice(0, 10);
}

function displayDate(value) {
  const iso = isoDate(value);
  if (!iso) return '';
  const [year, month, day] = iso.split('-');
  return `${day}/${month}/${year}`;
}

export function playSafeTip(value) {
  let text = String(value || '');
  const pairs = [
    [/\bcasa de apuestas\b/gi, 'medio deportivo'],
    [/\bapuestas?\b/gi, 'análisis'],
    [/\bapost(?:ar|e|ó|emos|aron)\b/gi, 'seguir'],
    [/\bcuotas?\b/gi, 'proyección'],
    [/\bodds\b/gi, 'proyección'],
    [/\bstake\b/gi, ''],
    [/\bpronósticos?\b/gi, 'consejos'],
    [/\bpronosticos?\b/gi, 'consejos'],
    [/\bOver\b/g, 'Más de'],
    [/\bUnder\b/g, 'Menos de'],
    [/\bBTTS\b/g, 'Ambos anotan'],
  ];
  pairs.forEach(([pattern, replacement]) => {
    text = text.replace(pattern, replacement);
  });
  return text.replace(/\s{2,}/g, ' ').trim();
}

export function bogotaDay(date = new Date()) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Bogota',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

export function formatDayLabel(iso) {
  if (!iso) return '';
  const months = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
  const [year, month, day] = iso.split('-');
  return `${Number(day)} ${months[Number(month) - 1] || ''}`.trim() || year;
}

export function fallbackIsoDay(rows, today = bogotaDay()) {
  const days = [...new Set(rows.map((row) => row.isoDay).filter(Boolean))]
    .filter((day) => day <= today)
    .sort();
  return days[days.length - 1] || today;
}

function normalizeState(raw) {
  const value = String(raw || '').toLowerCase();
  if (value === 'won' || value === 'ganada' || value === 'ganado') return 'ganada';
  if (value === 'lost' || value === 'perdida' || value === 'perdido') return 'perdida';
  if (value === 'void' || value === 'nula' || value === 'push') return 'nula';
  return 'pendiente';
}

export function mapPrediction(row, source) {
  const stateRaw = String(row.state || row.status || '').toLowerCase();
  const isLive =
    row.is_live === true ||
    (row.live_ended === false && ['live', 'en_vivo', 'inplay'].includes(stateRaw)) ||
    ['live', 'en_vivo', 'inplay'].includes(stateRaw);
  return {
    id: String(row.id),
    source,
    sport: row.sport || 'football',
    league: row.league || '',
    prediction: playSafeTip(row.prediction || ''),
    isoDay: isoDate(row.match_date || row.created_at),
    confidence: Number(row.confidence) || 0,
    odds: Number(row.odds) || 0,
    date: displayDate(row.match_date || row.created_at),
    hours: row.match_hour || row.hours || '',
    state: normalizeState(row.outcome || row.state || row.status),
    isLive: Boolean(isLive) && row.live_ended !== true,
    minute: row.minute ?? null,
    createdAt: row.created_at || null,
    homeTeam: {
      name: row.home_team_name || row.home_team || '',
      logo: row.home_team_logo || '',
      score: Number(row.home_goals) || 0,
    },
    awayTeam: {
      name: row.away_team_name || row.away_team || '',
      logo: row.away_team_logo || '',
      score: Number(row.away_goals) || 0,
    },
    value: Number(row.value) || 0,
    block: row.block === true,
  };
}

function toColumns(input) {
  const row = {};
  if (input.id) row.id = input.id;
  if (input.sport) row.sport = input.sport;
  if (input.league) row.league = input.league;
  if (input.homeTeam) {
    row.home_team_name = input.homeTeam.name || '';
    row.home_team_logo = input.homeTeam.logo || '';
    if (input.homeTeam.score != null) row.home_goals = input.homeTeam.score;
  }
  if (input.awayTeam) {
    row.away_team_name = input.awayTeam.name || '';
    row.away_team_logo = input.awayTeam.logo || '';
    if (input.awayTeam.score != null) row.away_goals = input.awayTeam.score;
  }
  if (input.prediction != null) row.prediction = input.prediction;
  if (input.confidence != null && input.confidence !== '') row.confidence = Number(input.confidence);
  if (input.odds != null && input.odds !== '') row.odds = Number(input.odds);
  if (input.date || input.match_date) row.match_date = isoDate(input.date || input.match_date);
  if (input.hours || input.match_hour) row.match_hour = input.hours || input.match_hour;
  if (input.state != null) row.state = input.state;
  return row;
}

export async function listPredictions(table) {
  const { data, error } = await db.from(table).select('*').limit(500);
  if (error) throw new Error(error.message || `No se pudo leer ${table}`);
  return (data || []).map((row) => mapPrediction(row, table));
}

export async function getPrediction(table, id) {
  const { data, error } = await db.from(table).select('*').eq('id', id).limit(1);
  if (error) throw new Error(error.message || `No se pudo leer ${table}`);
  const row = Array.isArray(data) ? data[0] : data;
  return row ? mapPrediction(row, table) : null;
}

async function loadPredictionMap(table) {
  const rows = await listPredictions(table);
  const map = {};
  rows.forEach((row) => {
    const { id, ...rest } = row;
    map[id] = rest;
  });
  return map;
}

function asSnapshot(map) {
  const hasRows = Object.keys(map).length > 0;
  return {
    exists: () => hasRows,
    val: () => (hasRows ? map : null),
  };
}

export async function readPredictionSnapshot(table) {
  return asSnapshot(await loadPredictionMap(table));
}

export function watchPredictionSnapshot(table, onSnapshot) {
  let stop = false;
  const emit = async () => {
    try {
      const snapshot = await readPredictionSnapshot(table);
      if (!stop) onSnapshot(snapshot);
    } catch (error) {
      console.error(`MatuDB ${table}:`, error);
    }
  };
  emit();
  const timer = setInterval(emit, 30000);
  return () => {
    stop = true;
    clearInterval(timer);
  };
}

export function watchPredictions(table, onRows) {
  return watchPredictionSnapshot(table, (snapshot) => {
    const data = snapshot.val() || {};
    onRows(Object.keys(data).map((id) => ({ id, ...data[id] })));
  });
}

export async function insertPrediction(table, input) {
  const id = input.id || newId();
  const row = toColumns({ ...input, id, sport: input.sport || 'football', state: input.state || 'pendiente' });
  const { data, error } = await db.from(table).insert(row);
  if (error) throw new Error(error.message || `No se pudo crear en ${table}`);
  return { id, data };
}

export async function updatePrediction(table, id, patch) {
  const row = toColumns(patch);
  const { error } = await db.from(table).eq('id', id).update(row);
  if (error) throw new Error(error.message || `No se pudo actualizar ${table}`);
}

export async function deletePrediction(table, id) {
  const { error } = await db.from(table).eq('id', id).delete();
  if (error) throw new Error(error.message || `No se pudo borrar ${table}`);
}

function shiftDay(iso, delta) {
  const date = new Date(`${iso}T12:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() + delta);
  return date.toISOString().slice(0, 10);
}

export function recentDays(today = bogotaDay(), lookback = 14) {
  const days = [];
  for (let i = lookback; i >= 0; i -= 1) days.push(shiftDay(today, -i));
  return days;
}

async function readDay(table, tier, iso) {
  const { data, error } = await db
    .from(table)
    .select('*')
    .eq('match_date', iso)
    .order('match_hour', { ascending: true })
    .limit(tier === 'vip' ? 300 : 500);
  if (error) throw new Error(error.message || `No se pudo leer ${table}`);
  return (data || []).map((row) => ({
    ...mapPrediction(row, table),
    tier,
    isoDay: iso,
  }));
}

async function readLive() {
  const { data, error } = await db
    .from(TABLES.live)
    .select('*')
    .order('match_date', { ascending: false })
    .limit(200);
  if (error) throw new Error(error.message || 'No se pudo leer en vivo');
  return (data || [])
    .map((row) => ({ ...mapPrediction(row, TABLES.live), tier: 'free' }))
    .filter((row) => row.isLive);
}

export async function loadPicksFeed(requestedDay) {
  const today = bogotaDay();
  const selected = requestedDay && requestedDay <= today ? requestedDay : today;
  const [free, vip, livePicks] = await Promise.all([
    readDay(TABLES.free, 'free', selected),
    readDay(TABLES.vip, 'vip', selected),
    readLive(),
  ]);
  return {
    dayPicks: [...free, ...vip],
    livePicks,
    days: recentDays(today, 14),
    selected,
    today,
    notice: null,
  };
}

function ignoreDuplicate(error) {
  const message = String(error?.message || '');
  return /duplicate|unique|conflict|already exists/i.test(message);
}

export async function loadSocial(userId) {
  if (!userId) return { liked: [], saved: [] };
  const [likes, saves] = await Promise.all([
    db.from(TABLES.follows).select('prediction_id').eq('client_id', userId).limit(500),
    db.from(TABLES.saves).select('prediction_id').eq('user_id', userId).limit(500),
  ]);
  if (likes.error) throw new Error(likes.error.message || 'No se pudieron leer los me gusta');
  if (saves.error) throw new Error(saves.error.message || 'No se pudieron leer los guardados');
  return {
    liked: (likes.data || []).map((row) => String(row.prediction_id)),
    saved: (saves.data || []).map((row) => String(row.prediction_id)),
  };
}

export async function setLike(userId, predictionId, liked) {
  if (liked) {
    const { error } = await db.from(TABLES.follows).insert({
      id: newId(),
      prediction_id: predictionId,
      client_id: userId,
      created_at: new Date().toISOString(),
    });
    if (error && !ignoreDuplicate(error)) throw new Error(error.message || 'No se pudo guardar el me gusta');
    return;
  }
  const { error } = await db
    .from(TABLES.follows)
    .eq('prediction_id', predictionId)
    .eq('client_id', userId)
    .delete();
  if (error) throw new Error(error.message || 'No se pudo quitar el me gusta');
}

export async function setSaved(userId, predictionId, saved) {
  if (saved) {
    const { error } = await db.from(TABLES.saves).insert({
      user_id: userId,
      prediction_id: predictionId,
      created_at: new Date().toISOString(),
    });
    if (error && !ignoreDuplicate(error)) throw new Error(error.message || 'No se pudo guardar');
    return;
  }
  const { error } = await db
    .from(TABLES.saves)
    .eq('user_id', userId)
    .eq('prediction_id', predictionId)
    .delete();
  if (error) throw new Error(error.message || 'No se pudo quitar de guardados');
}

export async function listComments(predictionId) {
  const { data, error } = await db
    .from(TABLES.comments)
    .select('id,prediction_id,user_id,author_name,author_initials,body,created_at')
    .eq('prediction_id', predictionId)
    .order('created_at', { ascending: true })
    .limit(200);
  if (error) throw new Error(error.message || 'No se pudieron leer los comentarios');
  return data || [];
}

export async function loadReactionCounts() {
  const [likes, comments] = await Promise.all([
    db.from(TABLES.follows).select('prediction_id').limit(5000),
    db.from(TABLES.comments).select('prediction_id').limit(5000),
  ]);
  const likeCounts = {};
  const commentCounts = {};
  (likes.data || []).forEach((row) => {
    const id = String(row.prediction_id || '');
    if (!id) return;
    likeCounts[id] = (likeCounts[id] || 0) + 1;
  });
  (comments.data || []).forEach((row) => {
    const id = String(row.prediction_id || '');
    if (!id) return;
    commentCounts[id] = (commentCounts[id] || 0) + 1;
  });
  return { likeCounts, commentCounts };
}

export async function addComment({ predictionId, userId, authorName, authorInitials, text }) {
  const row = {
    id: newId(),
    prediction_id: predictionId,
    user_id: userId,
    author_name: authorName || 'Cuenta',
    author_initials: authorInitials || 'U',
    body: text,
    created_at: new Date().toISOString(),
  };
  const { error } = await db.from(TABLES.comments).insert(row);
  if (error) throw new Error(error.message || 'No se pudo publicar el comentario');
  return row;
}

export async function listTickets(userId) {
  const { data, error } = await db
    .from(TABLES.tickets)
    .select('id,subject,body,status,created_at')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(50);
  if (error) throw new Error(error.message || 'No se pudieron leer los tickets');
  return data || [];
}

export async function createTicket({ userId, authorName, email, subject, body }) {
  const row = {
    id: newId(),
    user_id: userId,
    author_name: authorName || 'Cuenta',
    email: email || '',
    subject,
    body,
    status: 'abierto',
    created_at: new Date().toISOString(),
  };
  const { error } = await db.from(TABLES.tickets).insert(row);
  if (error) throw new Error(error.message || 'No se pudo registrar el ticket');
  return row;
}
